import React, { useState, useContext, useEffect } from "react";
import { CartContext } from "../context/CartContext";
import Footer from "./Footer";

const Checkout = () => {
  const { cart, billing, updateCustomer, clearCart } = useContext(CartContext);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alatpayLoaded, setAlatpayLoaded] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    phoneNumber2: "",
    address: "",
    email: "",
  });

  
  

  // Load Alatpay Script safely
  useEffect(() => {
    // Check if script is already loaded
    if (window.Alatpay) {
      setAlatpayLoaded(true);
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector('script[src="https://web.alatpay.ng/js/alatpay.js"]');
    if (existingScript) {
      existingScript.onload = () => setAlatpayLoaded(true);
      return;
    }

    // Create and load the script
    const script = document.createElement("script");
    script.src = "https://web.alatpay.ng/js/alatpay.js";
    script.async = true;
    
    script.onload = () => {
      if (window.Alatpay) {
        setAlatpayLoaded(true);
      } else {
        setError("Alatpay payment system failed to load");
      }
    };
    
    script.onerror = () => {
      setError("Failed to load payment system. Please refresh the page.");
    };

    document.body.appendChild(script);

    return () => {
      script.onload = null;
      script.onerror = null;
    };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  
  

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      throw new Error("First name is required");
    }
    if (!formData.lastName.trim()) {
      throw new Error("Last name is required");
    }
    if (!formData.email.trim()) {
      throw new Error("Email is required");
    }
    if (!formData.phoneNumber.trim()) {
      throw new Error("Phone number is required");
    }
    if (!formData.address.trim()) {
      throw new Error("Delivery address is required");
    }
    if (cart.length === 0) {
      throw new Error("Your cart is empty");
    }
  };

  const sendOrderConfirmation = async (paymentReference) => {
    try {
      const orderDetails = {
        access_key: process.env.REACT_APP_WEB3FORMS_KEY,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phoneNumber,
        address: formData.address,
        orderTotal: `₦${billing.total.toLocaleString()}`,
        paymentReference: paymentReference,
        items: cart.map(item => 
          `${item.name} × ${item.quantity} - ₦${item.price.toLocaleString()} (Total: ₦${(item.price * item.quantity).toLocaleString()})`
        ).join("\n"),
        subject: `New Order - ${paymentReference}`,
        form_type: "ORDER_CONFIRMATION"
      };
  
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderDetails),
      });
  
      if (!response.ok) {
        throw new Error("Failed to send order confirmation");
      }
  
      return await response.json();
    } catch (err) {
      console.error("Order confirmation error:", err);
      throw err;
    }
  };
  

  const handlePayment = async () => {
    try {
      // Validate form
      validateForm();
      
      setIsLoading(true);
      setError(null);

      // Update customer context
      updateCustomer({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone1: formData.phoneNumber,
        phone2: formData.phoneNumber2,
        address: formData.address,
        email: formData.email,
      });

      // Initialize Alatpay payment
      const popup = window.Alatpay.setup({
        apiKey: process.env.REACT_APP_ALATPAY_API_KEY,
        businessId: process.env.REACT_APP_ALATPAY_BUSINESS_ID,
        email: formData.email,
        phone: formData.phoneNumber,
        firstName: formData.firstName,
        lastName: formData.lastName,
        metadata: {
          cartItems: JSON.stringify(cart),
          deliveryAddress: formData.address
        },
        currency: "NGN",
        amount: billing.total,
        onTransaction: async (response) => {
          try {
            console.log("Payment response:", response);
            
            if (response.status === true) {
              await sendOrderConfirmation(response.orderId);
              clearCart();
              setPaymentSuccess(true);
            } else {
              throw new Error(response.message || "Payment failed. Please try again.");
            }
          } catch (err) {
            setError(err.message);
          } finally {
            setIsLoading(false);
          }
        },
        onClose: () => {
          console.log("Payment window closed");
          setIsLoading(false);
        }
      });

      // Show payment popup
      popup.show();
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div>
        <div className="min-h-[60vh] flex flex-col items-center justify-center bg-grey text-white">
          <div className="max-w-md p-8 bg-orange rounded-lg text-center">
            <h2 className="text-2xl font-bold mb-4">Order Successful!</h2>
            <p className="mb-6">
              Thank you for your purchase! We appreciate you choosing us to bring your space to life. 
              Our team will review your order and reach out soon to ensure everything is just right for your decor.
            </p>
            <button 
              onClick={() => window.location.href = "/"}
              className="mt-4 px-6 py-2 bg-orange border border-white text-white font-bold transition"
            >
              Back to Home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
}


  return (
    <div className="bg-grey">
      <div className="bg-grey text-white mb-[70px] p-8 pb-0 flex flex-col md:flex-row justify-center gap-12">
        {/* Billing Details Form */}
        <div className="w-full md:w-1/2">
          <h2 className="text-2xl font-bold mb-6">Billing details</h2>
          <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-1">First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full h-[50px] bg-grey border border-gray-600 rounded-md p-2"
                  required
                />
              </div>
              <div>
                <label className="block mb-1">Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full h-[50px] bg-grey border border-gray-600 rounded-md p-2"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block mb-1">Phone Number *</label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                className="w-full h-[50px] bg-grey border border-gray-600 rounded-md p-2"
                required
              />
            </div>
            <div>
              <label className="block mb-1">Phone Number 2 (Optional)</label>
              <input
                type="tel"
                name="phoneNumber2"
                value={formData.phoneNumber2}
                onChange={handleChange}
                className="w-full h-[50px] bg-grey border border-gray-600 rounded-md p-2"
              />
            </div>
            <div>
              <label className="block mb-1">Delivery Address *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full h-[50px] bg-grey border border-gray-600 rounded-md p-2"
                required
              />
            </div>
            <div>
              <label className="block mb-1">Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full h-[50px] bg-grey border border-gray-600 rounded-md p-2"
                required
              />
            </div>
          </form>
        </div>

        {/* Order Summary */}
        <div className="w-full md:w-1/3 bg-grey p-6 rounded-lg">
          <h2 className="text-xl font-bold mb-4">Order Summary</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}
          
          {cart.length === 0 ? (
            <p className="text-center text-lg py-4">Your cart is empty.</p>
          ) : (
            <>
              <div className="max-h-60 overflow-y-auto mb-4">
                {cart.map((item) => (
                  <div key={item.id} className="flex justify-between mb-2 py-2 border-b border-gray-600">
                    <p>
                      {item.name} × {item.quantity}
                    </p>
                    <p>₦{(item.price * item.quantity).toLocaleString()}</p>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-between border-t border-gray-600 py-3">
                <p className="font-medium">Subtotal</p>
                <p>₦{billing.total.toLocaleString()}</p>
              </div>
              
              <div className="flex justify-between text-lg font-bold my-3">
                <p>Total</p>
                <p className="text-orange">₦{billing.total.toLocaleString()}</p>
              </div>
              
              <button
                onClick={handlePayment}
                disabled={isLoading || !alatpayLoaded}
                className={`w-full mt-4 py-3 rounded-md transition-all ${
                  isLoading || !alatpayLoaded
                    ? "bg-gray-500 cursor-not-allowed"
                    : "bg-orange text-white hover:bg-orange-dark"
                }`}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  "Pay with Alatpay"
                )}
              </button>
              
              {!alatpayLoaded && (
                <p className="text-sm text-gray-400 mt-2 text-center">
                  Initializing payment system...
                </p>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Checkout;